import { MapPin } from "lucide-react";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { formatDate } from "@/utils/formatDate";
import type { ScheduleDay, ScheduleDayInfo, ScheduleUser } from "../types";

// ── Compact abbreviations ─────────────────────────────────────────────────────

export const COMPACT_ABBR: Record<string, string> = {
  present:            "B",
  uz_present:         "B",
  remote:             "Z",
  remote_pending:     "Z",
  remote_rejected:    "—",
  leave:              "UW",
  sick_leave:         "L4",
  sick_leave_pending: "L4",
  uz_absent:          "",
  uz_absent_explicit: "NB",
  day_off:            "",
  not_employed:       "",
  employment_ended:   "",
};

export const COMPACT_CELL: Record<string, { bg: string; text: string; border: string }> = {
  present:            { bg: "bg-green-100",  text: "text-green-800",   border: "border-green-300" },
  uz_present:         { bg: "bg-green-100",  text: "text-green-800",   border: "border-green-300" },
  remote:             { bg: "bg-blue-100",   text: "text-blue-800",    border: "border-blue-300"  },
  remote_pending:     { bg: "bg-blue-50",    text: "text-blue-600",    border: "border-blue-300"  },
  remote_rejected:    { bg: "bg-red-50",     text: "text-red-500",     border: "border-red-200"   },
  leave:              { bg: "bg-amber-100",  text: "text-amber-800",   border: "border-amber-300" },
  sick_leave:         { bg: "bg-purple-100", text: "text-purple-800",  border: "border-purple-300"},
  sick_leave_pending: { bg: "bg-purple-50",  text: "text-purple-600",  border: "border-purple-300"},
  uz_absent:          { bg: "bg-transparent", text: "text-gray-300",   border: "border-transparent"},
  uz_absent_explicit: { bg: "bg-orange-50", text: "text-orange-600",   border: "border-orange-200"},
  day_off:            { bg: "bg-transparent", text: "text-gray-300",   border: "border-transparent"},
  not_employed:       { bg: "bg-transparent", text: "text-gray-300",   border: "border-transparent"},
  employment_ended:   { bg: "bg-transparent", text: "text-gray-300",   border: "border-transparent"},
};

// ── Hours formatting ──────────────────────────────────────────────────────────

// Returns just the start hour for a compact hint, e.g. "8" or "8:30"
function fmtRemoteHourCompact(info: ScheduleDayInfo): string | null {
  const raw =
    info.hours_ranges && info.hours_ranges.length > 0
      ? info.hours_ranges[0].start
      : (info.start_hour ?? null);
  if (!raw) return null;
  const [hStr, mStr] = raw.split(":");
  return mStr === "00" ? `${parseInt(hStr, 10)}` : `${parseInt(hStr, 10)}:${mStr}`;
}

// Extracts duration hint from hourly leave note, e.g. "Urlop godzinowy (2h 30min)" → "2h30"
function fmtLeaveHourHint(info: ScheduleDayInfo): string | null {
  if (info.type !== "leave") return null;
  const match = info.note.match(/\(([^)]+)\)/);
  if (!match) return null;
  return match[1].replace(" ", "");
}

export const FULL_LABEL: Record<string, string> = {
  present:            "W biurze",
  uz_present:         "W biurze",
  remote:             "Praca zdalna",
  remote_pending:     "Zdalna (oczekuje)",
  remote_rejected:    "Zdalna odrzucona",
  leave:              "Urlop",
  sick_leave:         "L4",
  sick_leave_pending: "L4 (oczekuje)",
  uz_absent:          "—",
  uz_absent_explicit: "Nieobecny",
  day_off:            "Dzień wolny",
  not_employed:       "Przed rozpoczęciem pracy",
  employment_ended:   "Po zakończeniu pracy",
};

const POLISH_DAYS_COMPACT = ["Pn","Wt","Śr","Cz","Pt","So","Nd"];

export function getDayDisplayType(info: ScheduleDayInfo): string {
  if (info.type === "uz_absent" && info.entry_id) return "uz_absent_explicit";
  if (info.type === "remote" && info.status === "pending") return "remote_pending";
  if (info.type === "remote" && info.status === "rejected") return "remote_rejected";
  if (info.type === "sick_leave" && info.status === "pending") return "sick_leave_pending";
  return info.type;
}

// ── Props ─────────────────────────────────────────────────────────────────────

type RenderItem =
  | { kind: "header"; branchId: string | null; branchName: string }
  | { kind: "user"; schedUser: ScheduleUser; ri: number };

interface Props {
  days: ScheduleDay[];
  renderItems: RenderItem[];
  todayStr: string;
  currentUserId: string | undefined;
  /** Only directors / coordinators / HR / accountants / CEO see tooltips on others' cells */
  canSeeTooltip?: boolean;
  onCellClick: (schedUser: ScheduleUser, dateStr: string, info: ScheduleDayInfo) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ScheduleCompactView({ days, renderItems, todayStr, currentUserId, canSeeTooltip = false, onCellClick }: Props) {
  return (
    <table className="border-collapse text-[10px] sm:text-xs">
        {/* Sticky header */}
        <thead className="sticky top-0 z-30">
          <tr className="border-b-2 border-gray-200 dark:border-gray-600">
            {/* Employee column header */}
            <th className="sticky left-0 z-20 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 px-2 sm:px-3 py-2 text-left font-mono text-[9px] uppercase tracking-widest text-gray-400 dark:text-gray-500 min-w-[140px] sm:min-w-[180px]">
              Pracownik
            </th>

            {/* Day headers */}
            {days.map((day) => {
              const d = new Date(day.date + "T00:00:00");
              const isToday = day.date === todayStr;
              const dayOfWeek = d.getDay(); // 0=Sun 6=Sat
              const isMonday = dayOfWeek === 1;
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
              const dayShort = POLISH_DAYS_COMPACT[dayOfWeek === 0 ? 6 : dayOfWeek - 1];

              return (
                <th
                  key={day.date}
                  title={formatDate(day.date)}
                  className={[
                    "w-6 sm:w-7 min-w-[22px] sm:min-w-[26px] px-0 py-1.5 text-center font-semibold select-none",
                    isMonday ? "border-l-2 border-l-gray-300 dark:border-l-gray-500" : "",
                    isToday
                      ? "bg-lime-50 text-[var(--color-primary)] dark:text-blue-300"
                      : isWeekend
                      ? "bg-gray-50 dark:bg-gray-800/60 text-gray-400 dark:text-gray-600"
                      : "bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400",
                  ].filter(Boolean).join(" ")}
                >
                  <div className="text-[8px] sm:text-[9px] leading-none opacity-70">{dayShort}</div>
                  <div className={[
                    "text-[10px] sm:text-xs font-bold leading-snug",
                    isToday ? "text-[var(--color-primary)] dark:text-blue-300" : "",
                    day.is_holiday ? "text-red-400 dark:text-red-500" : "",
                  ].filter(Boolean).join(" ")}>
                    {d.getDate()}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {renderItems.map((item) => {
            // Branch header row
            if (item.kind === "header") {
              return (
                <tr key={`branch-${item.branchId ?? "none"}`} className="bg-gray-100 dark:bg-gray-700/60">
                  <td className="px-3 py-1 sticky left-0 z-10 bg-gray-100 dark:bg-gray-700/60 border-r border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-1.5">
                      <MapPin size={10} className="text-[var(--color-primary)] flex-shrink-0" />
                      <span className="text-[9px] font-semibold uppercase tracking-widest text-gray-600 dark:text-gray-300">
                        {item.branchName}
                      </span>
                    </div>
                  </td>
                  <td colSpan={days.length} className="bg-gray-100 dark:bg-gray-700/60" />
                </tr>
              );
            }

            // Employee row
            const { schedUser, ri } = item;
            const isCurrentUser = schedUser.id === currentUserId;
            const isInactive = schedUser.is_active === false;
            const rowBg = isInactive
              ? "bg-red-50 dark:bg-red-900/15"
              : isCurrentUser
              ? "bg-blue-50 dark:bg-blue-900/20"
              : ri % 2 === 1
              ? "bg-gray-50 dark:bg-gray-800/60"
              : "bg-white dark:bg-gray-800";
            const nameBg = isInactive
              ? "bg-red-50 dark:bg-red-900/15"
              : isCurrentUser
              ? "bg-blue-50 dark:bg-blue-900/20"
              : ri % 2 === 1
              ? "bg-gray-50 dark:bg-gray-800/60"
              : "bg-white dark:bg-gray-800";

            return (
              <tr
                key={schedUser.id}
                className={`border-b border-gray-100 dark:border-gray-700/40 ${rowBg}`}
              >
                {/* Sticky employee name cell */}
                <td
                  className={`sticky left-0 z-10 border-r border-gray-200 dark:border-gray-700 px-2 sm:px-3 py-1 ${nameBg}`}
                >
                  <div className="flex items-center gap-1.5 min-w-0" title={isInactive ? "Pracownik nieaktywny (zwolniony)" : undefined}>
                    <UserAvatar
                      fullName={schedUser.name}
                      profilePictureUrl={schedUser.profile_picture_url}
                      size="xs"
                      className="rounded-full flex-shrink-0"
                      initialsClassName="bg-[var(--color-primary)] text-white font-bold text-[8px]"
                    />
                    <span className={`font-medium text-[10px] sm:text-xs whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px] sm:max-w-[150px] ${isInactive ? "text-red-700 dark:text-red-300" : "text-gray-800 dark:text-gray-200"}`}>
                      {schedUser.name}
                    </span>
                  </div>
                </td>

                {/* Day cells */}
                {days.map((day) => {
                  const info = schedUser.days[day.date];
                  const d = new Date(day.date + "T00:00:00");
                  const dayOfWeek = d.getDay();
                  const isMonday = dayOfWeek === 1;
                  const isToday = day.date === todayStr;
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

                  if (!info) {
                    return (
                      <td
                        key={day.date}
                        className={[
                          "w-6 sm:w-7 py-1",
                          isMonday ? "border-l-2 border-l-gray-200 dark:border-l-gray-600" : "",
                          isToday ? "border-l-2 border-l-[var(--color-primary)]/40" : "",
                          isWeekend ? "bg-gray-50/60 dark:bg-gray-800/30" : "",
                        ].filter(Boolean).join(" ")}
                      />
                    );
                  }

                  const displayType = getDayDisplayType(info);
                  const cell = COMPACT_CELL[displayType] ?? COMPACT_CELL.day_off;
                  const abbr = COMPACT_ABBR[displayType] ?? "";
                  const tooltip = FULL_LABEL[displayType] ?? "";

                  const isClickable =
                    schedUser.id === currentUserId &&
                    info.type !== "day_off" &&
                    info.type !== "leave" &&
                    info.type !== "sick_leave" &&
                    (((schedUser.is_uz || schedUser.employment_fraction_numerator < schedUser.employment_fraction_denominator) &&
                      (info.type === "uz_present" ||
                        info.type === "uz_absent" ||
                        (info.type === "remote" &&
                          (info.status === "pending" || info.status === "approved")))) ||
                      (!(schedUser.is_uz || schedUser.employment_fraction_numerator < schedUser.employment_fraction_denominator) &&
                        (info.type === "present" ||
                          (!!schedUser.is_b2b && info.type === "remote" && !info.entry_id) ||
                          (!!schedUser.is_b2b && info.type === "uz_absent" && !!info.entry_id) ||
                          (info.type === "remote" &&
                            (info.status === "pending" || info.status === "approved")))));

                  return (
                    <td
                      key={day.date}
                      title={
                        schedUser.id === currentUserId
                          ? tooltip + (info.note ? ` — ${info.note}` : "")
                          : canSeeTooltip
                          ? tooltip + (info.note ? ` — ${info.note}` : "")
                          : undefined
                      }
                      className={[
                        "w-6 sm:w-7 px-0 py-1 text-center",
                        isMonday ? "border-l-2 border-l-gray-200 dark:border-l-gray-600" : "",
                        isToday ? "border-l-2 border-l-[var(--color-primary)]/50" : "",
                        isWeekend && info.type === "day_off" ? "bg-gray-50/60 dark:bg-gray-800/30" : "",
                      ].filter(Boolean).join(" ")}
                    >
                      {info.type !== "day_off" && (
                        <div
                          onClick={() => isClickable && onCellClick(schedUser, day.date, info)}
                          className={[
                            "mx-auto flex flex-col items-center justify-center rounded",
                            "w-5 sm:w-6 h-5 sm:h-6 overflow-hidden",
                            "text-[9px] sm:text-[10px] font-bold border leading-none",
                            cell.bg, cell.text, cell.border,
                            isClickable ? "cursor-pointer hover:opacity-70 transition-opacity" : "",
                            displayType === "remote_pending" || displayType === "sick_leave_pending"
                              ? "border-dashed opacity-80"
                              : "",
                          ].filter(Boolean).join(" ")}
                        >
                          {(() => {
                            const hourHint =
                              (displayType === "remote" || displayType === "remote_pending")
                                ? fmtRemoteHourCompact(info)
                                : fmtLeaveHourHint(info);
                            return hourHint ? (
                              <>
                                <span className="leading-none">{abbr}</span>
                                <span className="text-[6px] leading-none font-normal opacity-80">
                                  {hourHint}
                                </span>
                              </>
                            ) : (
                              <span>{abbr}</span>
                            );
                          })()}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
  );
}
